import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, FieldPath, FieldValues, useForm, UseFormReturn, useWatch } from 'react-hook-form';
import { useAuth } from '../../../context/Auth';
import { useQuery } from '@tanstack/react-query';
import { appAgentService } from '../../../services/central-api/app-agent';
import { ActivityIndicator, Modal, View } from 'react-native';
import { createStyleSheet, UnistylesRuntime, useStyles } from 'react-native-unistyles';
import PatrolIndicatorDropdown from '../../../components/app-agent/PatrolIndicatorDropdown';
import FormInput from '../../../components/FormInput';
import { translate } from '../../../services/translate';
import { applyMask } from '../../../components/helpers/documents';
import TimePicker from 'react-time-picker';
import MyAppText from '../../../components/MyAppText';
import { ScrollView } from 'react-native-web-hover';
import FormActions from '../../../components/FormActions';
import Toast from 'react-native-toast-message';
import { UserNotInUnit } from '../../../services/central-api/base-service';
import moment from 'moment';
import MyDatePicker from '../../../components/MyDatePicker';
import CustomButton from '../../../components/CustomButton';
import { faLocationDot } from '@fortawesome/free-solid-svg-icons';
import { centralAPI } from '../../../services/central-api';
import LocationSelector from '../../../components/LocationSelector';
import Map from '../../../components/Map';

interface Props {
    isModalVisible: boolean;
    onClose: () => void;
    onConfirm: () => void;
    activityId?: number;
    agentUnit?: AppAgentUnit | null;
}

interface ActivityAndServiceForm {
    cep: string;
    address: string;
    number: number | undefined;
    date?: Date;
    startTime: string;
    endTime: string;
    actionsQty: number | undefined;
    publicQty: number | undefined;
    documentNumber: number | undefined;
    indicatorId: number;
    latitude: string;
    longitude: string;
}

export default function ActivitiesAndServicesModal({ isModalVisible, onClose, onConfirm, activityId, agentUnit }: Props) {
    const { styles, theme } = useStyles(styleSheet);
    const { user: authenticatedUser } = useAuth();

    const dateIsNeeded = useMemo(() => {
        if (!agentUnit?.definitiveStart) {
            return false;
        }
        return moment().diff(moment(agentUnit.definitiveStart), 'day') >= 1;
    }, [agentUnit?.definitiveStart]);

    const [pin, setPin] = useState<LatLng | undefined>(undefined);
    const [resolvingLocation, setResolvingLocation] = useState<boolean>(false);
    const [sendingData, setSendingData] = useState<boolean>(false);
    const settingCepInternallyRef = useRef('');

    const defaultEmptyValues: ActivityAndServiceForm = {
        cep: '',
        address: '',
        number: undefined,
        date: undefined,
        startTime: '',
        endTime: '',
        actionsQty: undefined,
        publicQty: undefined,
        documentNumber: undefined,
        indicatorId: 0,
        latitude: '',
        longitude: ''
    };

    const form = useForm<ActivityAndServiceForm>({
        defaultValues: defaultEmptyValues,
        mode: 'onChange'
    });

    useEffect(() => {
        form.register('address', { required: true });
        form.register('latitude', { required: true });
        form.register('longitude', { required: true });
    }, [form]);

    const queryResult = useQuery({
        queryKey: ['getActivityAndServiceById', activityId, isModalVisible],
        queryFn: () => appAgentService.getActivityAndServiceById(activityId!),
        enabled: !!authenticatedUser && !!activityId && isModalVisible,
    });

    const isEnabled = !!authenticatedUser;
    const isLoading = isEnabled && queryResult.isFetching;
    const { data } = queryResult;

    function clearValues() {
        form.reset(defaultEmptyValues);
    }

    useEffect(() => {
        if (!authenticatedUser || !data) {
            form.reset({
                ...defaultEmptyValues,
                date: dateIsNeeded ? moment().toDate() : undefined,
            });
            setPin(undefined);
            return;
        }

        settingCepInternallyRef.current = data.cep;
        form.reset({
            cep: data.cep,
            address: data.address,
            number: data.number,
            date: dateIsNeeded
                ? (data.startDate ? moment(data.startDate).toDate() : moment().toDate())
                : undefined,
            startTime: moment(data.startDate).format('HH:mm'),
            endTime: moment(data.endDate).format('HH:mm'),
            actionsQty: data.actionsQty,
            publicQty: data.publicQty,
            documentNumber: data.documentNumber,
            indicatorId: data.indicatorId,
            latitude: data.latitude,
            longitude: data.longitude,
        });
        if (data.latitude && data.longitude) {
            setPin({ lat: Number(data.latitude), lng: Number(data.longitude) });
        }
    }, [data, authenticatedUser, dateIsNeeded]);

    function formatDuration(totalMinutes: number): string {
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;

        if (hours === 0) {
            return `${minutes}min`;
        }
        if (minutes === 0) {
            return `${hours}h`;
        }
        return `${hours}h${minutes}min`;
    }

    function anchorEndTime(endTime: string, start: moment.Moment): moment.Moment {
        const [hours, minutes] = endTime.split(':').map(Number);
        let end = start.clone().set({ hour: hours, minute: minutes, second: 0, millisecond: 0 });

        if (end.isBefore(start)) {
            end = end.add(1, 'day');
        }

        return end;
    }

    function computeEffectiveRange(startTime?: string, endTime?: string, date?: Date) {
        if (!startTime || !endTime) {
            return null;
        }

        let start: moment.Moment | null = null;

        if (dateIsNeeded) {
            if (!date) {
                return null;
            }
            const [hours, minutes] = startTime.split(':').map(Number);
            start = moment(date).set({ hour: hours, minute: minutes, second: 0, millisecond: 0 });
        } else if (agentUnit?.definitiveStart) {
            const shiftStart = moment(agentUnit.definitiveStart);
            const [hours, minutes] = startTime.split(':').map(Number);

            start = shiftStart.clone().set({ hour: hours, minute: minutes, second: 0, millisecond: 0 });

            if (start.isBefore(shiftStart)) {
                start = start.add(1, 'day');
            }
        }

        if (!start) {
            return null;
        }

        const end = anchorEndTime(endTime, start);

        return { start, end };
    }

    async function formSubmit() {
        setSendingData(true);
        try {
            const values = form.getValues();
            const dates = computeEffectiveRange(values.startTime, values.endTime, values.date);
            const resolvedDate = dates?.start
                ? moment(dates.start).format('YYYY-MM-DD')
                : undefined;

            if (activityId) {
                await appAgentService.updateActivityAndService({
                    id: activityId,
                    cep: values.cep ? values.cep : undefined,
                    address: values.address ? values.address : undefined,
                    number: values.number ? Number(values.number!) : undefined,
                    date: resolvedDate,
                    startTime: values.startTime ? values.startTime : undefined,
                    endTime: values.endTime ? values.endTime : undefined,
                    actionsQty: values.actionsQty ? Number(values.actionsQty!) : undefined,
                    publicQty: values.publicQty ? Number(values.publicQty!) : undefined,
                    documentNumber: values.documentNumber ? Number(values.documentNumber) : undefined,
                    indicatorId: values.indicatorId ? values.indicatorId : undefined,
                    latitude: values.latitude ? values.latitude : undefined,
                    longitude: values.longitude ? values.longitude : undefined,
                });
            } else {
                await appAgentService.createActivityAndService({
                    cep: values.cep,
                    address: values.address,
                    number: values.number ? Number(values.number!) : undefined,
                    date: resolvedDate,
                    startTime: values.startTime,
                    endTime: values.endTime,
                    actionsQty: Number(values.actionsQty!),
                    publicQty: Number(values.publicQty!),
                    documentNumber: values.documentNumber ? Number(values.documentNumber) : undefined,
                    indicatorId: values.indicatorId,
                    latitude: values.latitude,
                    longitude: values.longitude,
                });
            }

            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });
            clearValues();
            onClose();
            onConfirm();
        } catch (err) {
            if (err instanceof UserNotInUnit) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate('notActiveInUnit'),
                });
            }

            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        } finally {
            setSendingData(false);
        }
    }

    const values = useWatch({ control: form.control });
    function makeSetStateHandler<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>>(
        form: UseFormReturn<TFieldValues>,
        fieldName: TName
    ) {
        return (value: React.SetStateAction<TFieldValues[TName]>) => {
            const resolved = typeof value === 'function'
                ? (value as (prev: TFieldValues[TName]) => TFieldValues[TName])(form.getValues(fieldName))
                : value;

            form.setValue(fieldName, resolved, {
                shouldValidate: true,
                shouldDirty: true,
            });
        };
    }

    const activityRange = useMemo(
        () => computeEffectiveRange(values.startTime, values.endTime, values.date),
        [values.startTime, values.endTime, values.date, dateIsNeeded, agentUnit?.definitiveStart, data?.startDate]
    );

    const activityRangeLabel = useMemo(() => {
        if (!activityRange) {
            return null;
        }

        const { start, end } = activityRange;
        const crossesMidnight = !start.isSame(end, 'day');
        const isSameValue = start.isSame(end);
        const durationMinutes = end.diff(start, 'minutes');

        return {
            rangeText: crossesMidnight
                ? `${start.format('DD/MM HH:mm')} → ${end.format('DD/MM HH:mm')}`
                : `${start.format('DD/MM')} · ${start.format('HH:mm')} → ${end.format('HH:mm')}`,
            durationText: formatDuration(durationMinutes),
            crossesMidnight,
            isSameValue
        };
    }, [activityRange]);

    window.sendLocation = (lat, lng) => {
        handlePinChange({ lat: Number(lat), lng: Number(lng) });
    };

    function getCurrentPosition() {
        if (window.AndroidBridge?.getLocation) {
            window.AndroidBridge?.getLocation();
        } else if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition((pos) => {
                handlePinChange({ lat: pos.coords.latitude, lng: pos.coords.longitude });
            });
        }
    }

    interface AddressByPlaceDetails {
        streetNumber?: string;
        streetName?: string;
        neighborhood?: string;
        city?: string;
        state?: string;
        country?: string;
        postalCode?: string;
        formattedAddress?: string;
        latitude?: number;
        longitude?: number;
    }

    function applyAddressDetails(details: AddressByPlaceDetails) {
        const streetAddress = details.streetName ?? details.formattedAddress;
        if (streetAddress) {
            form.setValue('address', streetAddress, { shouldValidate: true, shouldDirty: true });
        }

        if (details.postalCode) {
            const cleanPostalCode = details.postalCode.replace(/\D/g, '');
            settingCepInternallyRef.current = cleanPostalCode;
            form.setValue('cep', cleanPostalCode, { shouldValidate: true, shouldDirty: true });
        }

        if (details.streetNumber) {
            form.setValue('number', Number(details.streetNumber), { shouldValidate: true, shouldDirty: true });
        }
    }

    async function handlePinChange(pos: LatLng) {
        if (values.latitude === pos.lat.toString() && values.longitude === pos.lng.toString()) {
            return;
        }

        setPin(pos);
        form.setValue('latitude', pos.lat.toString(), { shouldValidate: true, shouldDirty: true });
        form.setValue('longitude', pos.lng.toString(), { shouldValidate: true, shouldDirty: true });

        setResolvingLocation(true);
        try {
            const details = await centralAPI.getAddressByPlace({
                latitude: pos.lat,
                longitude: pos.lng,
                getFullObject: true,
            }) as AddressByPlaceDetails;

            if (details) {
                applyAddressDetails(details);
                if (!details.postalCode) {
                    Toast.show({ type: 'sentinelxWarning', text1: translate('cepMustBeAddedManually') });
                }
            }
        } catch (err) {
            console.error(err);
        } finally {
            setResolvingLocation(false);
        }
    }

    async function fetchLocationByCep(cep: string) {
        setResolvingLocation(true);
        try {
            const details = await centralAPI.getFullPlaceByAddress({
                address: cep,
                getFullObject: true,
            }) as AddressByPlaceDetails;

            if (!details || details.latitude === undefined || details.longitude === undefined) {
                Toast.show({ type: 'sentinelxWarning', text1: translate('cepNotFound') });
                return;
            }

            const pos = { lat: details.latitude, lng: details.longitude };
            setPin(pos);
            form.setValue('latitude', pos.lat.toString(), { shouldValidate: true, shouldDirty: true });
            form.setValue('longitude', pos.lng.toString(), { shouldValidate: true, shouldDirty: true });
            applyAddressDetails(details);
        } catch (err) {
            console.error(err);
            Toast.show({ type: 'sentinelxError', text1: translate('unexpectedError') });
        } finally {
            setResolvingLocation(false);
        }
    }

    const cepDigits = (values.cep ?? '').replace(/\D/g, '');
    useEffect(() => {
        if (settingCepInternallyRef.current === cepDigits) {
            return;
        }

        if (cepDigits.length === 8) {
            fetchLocationByCep(cepDigits);
        }
    }, [cepDigits]);

    return (
        <Modal
            visible={isModalVisible}
            animationType='slide'
            onRequestClose={() => {
                clearValues();
                onClose();
            }}
            transparent={true}
        >
            <View style={styles.container}>
                <View style={[styles.modalBody, isLoading && { height: '85%' }]}>
                    <MyAppText style={styles.header}>{translate('activityAndService')}</MyAppText>

                    <ScrollView
                        showsVerticalScrollIndicator={false}
                        contentContainerStyle={[{ flexDirection: 'column', gap: 8 }, isLoading && { flex: 1 }]}
                    >
                        {isLoading
                            ? <View style={styles.loadingContainer}>
                                <ActivityIndicator size='large' color={theme.colors.iconColor} />
                            </View>
                            : <>

                                <Controller
                                    control={form.control}
                                    name="indicatorId"
                                    rules={{
                                        validate: (value) => value !== 0 || translate('required_field')
                                    }}
                                    render={({ field }) => (
                                        <PatrolIndicatorDropdown
                                            value={field.value}
                                            setValue={makeSetStateHandler(form, 'indicatorId')}
                                            zIndex={3}
                                            required
                                            labelStyle={styles.fieldsLabelStyle}
                                            viewStyle={styles.itemView}
                                        />
                                    )}
                                />

                                <View style={{ flexDirection: 'row', gap: 12, alignItems: 'flex-end', zIndex: 2 }}>
                                    <LocationSelector
                                        address={values.address ?? ''}
                                        onAddressChange={makeSetStateHandler(form, 'address')}
                                        onPinChange={(pos) => {
                                            if (!pos) {
                                                return;
                                            }
                                            handlePinChange(pos);
                                        }}
                                        viewStyle={[styles.itemView, { flex: 2, zIndex: 2 }]}
                                        labelStyle={styles.fieldsLabelStyle}
                                        label={translate('street')}
                                        required
                                    />
                                    <CustomButton
                                        onPress={getCurrentPosition}
                                        icon={faLocationDot}
                                        contentContainerStyle={{ alignSelf: 'flex-end' }}
                                    />
                                </View>
                                <View style={styles.mapContainer}>
                                    <Map
                                        dynamicMarker={pin}
                                        useDynamicMarker={true}
                                        markerChanged={(pos) => {
                                            handlePinChange(pos);
                                        }}
                                        zoom={16}
                                    />
                                    {resolvingLocation && (
                                        <View style={styles.mapLoadingOverlay}>
                                            <ActivityIndicator size='small' color={theme.colors.iconColor} />
                                        </View>
                                    )}
                                </View>

                                <FormInput
                                    form={form}
                                    fieldName='cep'
                                    label={translate('cep')}
                                    maxLength={9}
                                    mode='NUMBER'
                                    mask={(val) => applyMask(val, 'cep')}
                                    labelStyle={styles.fieldsLabelStyle}
                                    viewStyle={styles.itemView}
                                    required
                                    disabled={resolvingLocation}
                                />
                                <FormInput
                                    form={form}
                                    fieldName='number'
                                    label={translate('number')}
                                    mode='NUMBER'
                                    labelStyle={styles.fieldsLabelStyle}
                                    viewStyle={styles.itemView}
                                />
                                {dateIsNeeded && <MyDatePicker
                                    form={form}
                                    dateFieldName='date'
                                    required
                                    label={translate('activityDate')}
                                    hintText={translate('activityDateHint')}
                                    labelStyle={styles.fieldsLabelStyle}
                                    viewStyle={styles.itemView}
                                    minDate={agentUnit?.definitiveStart ? moment(agentUnit?.definitiveStart).toDate() : undefined}
                                    maxDate={moment().toDate()}
                                    zIndex={2}
                                />}
                                <View style={{ flexDirection: 'row', gap: 12 }}>
                                    <Controller
                                        control={form.control}
                                        name="startTime"
                                        rules={{ required: translate('required_field') }}
                                        render={({ field, fieldState }) => (
                                            <View style={{ flexDirection: 'column', gap: 8, flex: 1 }}>
                                                <View style={[{ flexDirection: 'row', columnGap: 5 }]}>
                                                    <MyAppText style={styles.fieldsLabelStyle}>{translate('startTime')}</MyAppText>
                                                    <MyAppText style={{ color: theme.colors.errors['300'] }}>(*)</MyAppText>
                                                </View>
                                                <TimePicker
                                                    disableClock={true}
                                                    format='HH:mm'
                                                    locale='pt-BR'
                                                    maxDetail='minute'
                                                    className={['light', 'lightAlt'].includes(UnistylesRuntime.themeName) ? ['inputColor', `inputColorLight`] : ['inputColor', `inputColorDark`]}
                                                    value={field.value}
                                                    clearIcon={null}
                                                    onChange={(value) => {
                                                        if (value) {
                                                            field.onChange(value);
                                                            form.trigger('endTime');
                                                        }
                                                    }}
                                                />
                                                {fieldState.error && (
                                                    <MyAppText style={styles.error}>{fieldState.error.message}</MyAppText>
                                                )}
                                            </View>
                                        )}
                                    />
                                    <Controller
                                        control={form.control}
                                        name="endTime"
                                        rules={{
                                            required: translate('required_field'),
                                            validate: (value) => {
                                                const startTime = form.getValues('startTime');
                                                if (startTime && value && startTime === value) {
                                                    return false;
                                                }
                                                return true;
                                            },
                                        }}
                                        render={({ field }) => (
                                            <View style={{ flexDirection: 'column', gap: 8, flex: 1 }}>
                                                <View style={[{ flexDirection: 'row', columnGap: 5 }]}>
                                                    <MyAppText style={styles.fieldsLabelStyle}>{translate('endTime')}</MyAppText>
                                                    <MyAppText style={{ color: theme.colors.errors['300'] }}>(*)</MyAppText>
                                                </View>
                                                <TimePicker
                                                    disableClock={true}
                                                    format='HH:mm'
                                                    locale='pt-BR'
                                                    maxDetail='minute'
                                                    className={['light', 'lightAlt'].includes(UnistylesRuntime.themeName) ? ['inputColor', `inputColorLight`] : ['inputColor', `inputColorDark`]}
                                                    value={field.value}
                                                    clearIcon={null}
                                                    onChange={(value) => {
                                                        if (value) {
                                                            field.onChange(value);
                                                            form.trigger('startTime');
                                                        }
                                                    }}
                                                />
                                            </View>
                                        )}
                                    />
                                </View>
                                {activityRangeLabel && (
                                    <View style={styles.rangePreview}>
                                        <MyAppText style={styles.rangePreviewText}>{activityRangeLabel.rangeText}</MyAppText>
                                        <MyAppText style={styles.rangePreviewDuration}>
                                            {translate('duration')}: {activityRangeLabel.durationText}
                                        </MyAppText>
                                        {activityRangeLabel.isSameValue
                                            ? <MyAppText style={styles.error}>
                                                {translate('startEndTimeCannotBeEqual')}
                                            </MyAppText>
                                            : activityRangeLabel.crossesMidnight && (
                                                <MyAppText style={styles.rangePreviewWarning}>
                                                    {translate('activityCrossesMidnightWarning')}
                                                </MyAppText>
                                            )}
                                    </View>
                                )}
                                <View style={{ flexDirection: 'row', gap: 12, alignItems: 'center' }}>
                                    <FormInput
                                        form={form}
                                        fieldName='actionsQty'
                                        label={translate('actionsQuantity')}
                                        mode='NUMBER'
                                        labelStyle={styles.fieldsLabelStyle}
                                        viewStyle={styles.itemView}
                                        required
                                    />
                                    <FormInput
                                        form={form}
                                        fieldName='publicQty'
                                        label={translate('publicQuantity')}
                                        mode='NUMBER'
                                        labelStyle={[styles.fieldsLabelStyle, { fontSize: 13 }]}
                                        viewStyle={styles.itemView}
                                        required
                                    />
                                </View>
                                <FormInput
                                    form={form}
                                    fieldName='documentNumber'
                                    label={translate('document')}
                                    mode='NUMBER'
                                    labelStyle={[styles.fieldsLabelStyle, { fontSize: 13 }]}
                                    viewStyle={styles.itemView}
                                />
                            </>}
                    </ScrollView>
                    <FormActions
                        viewStyle={{ justifyContent: 'space-around' }}
                        buttonStyle={{ paddingHorizontal: 16, paddingVertical: 9, }}
                        buttonContentContainerStyle={{ flex: 1 }}
                        cancelText='goBack'
                        confirmText='save'
                        onSubmit={formSubmit}
                        onClose={() => {
                            clearValues();
                            onClose();
                        }}
                        disabled={isLoading || !form.formState.isValid}
                        isLoading={isLoading || sendingData}
                    />
                </View>
            </View>
        </Modal>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    container: {
        justifyContent: 'flex-end',
        flex: 1
    },
    modalBody: {
        backgroundColor: theme.colors.card.background,
        borderTopLeftRadius: 20,
        borderTopRightRadius: 20,
        padding: 20,
        maxHeight: '85%',
        gap: 16
    },
    header: {
        fontSize: 18,
        fontWeight: '700',
        flexWrap: 'wrap',
        color: theme.colors.modal.header,
    },
    fieldsLabelStyle: {
        color: theme.colors.neutral['500'],
        fontWeight: '600',
        fontSize: 14,
    },
    itemView: {
        gap: 8,
        flex: 1
    },
    loadingContainer: {
        flexDirection: 'column',
        flex: 1,
        height: '100%',
        justifyContent: 'center',
        alignItems: 'center'
    },
    mapContainer: {
        height: 200,
        borderRadius: 8,
        overflow: 'hidden',
        borderWidth: 1,
        borderColor: theme.colors.card.border,
        zIndex: 1
    },
    mapLoadingOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.15)',
        alignItems: 'center',
        justifyContent: 'center',
    },
    error: {
        color: theme.colors.errors['300'],
        fontSize: 10,
    },
    rangePreview: {
        borderWidth: 1,
        borderColor: theme.colors.card.border,
        borderRadius: 8,
        paddingHorizontal: 12,
        paddingVertical: 10,
        gap: 4,
    },
    rangePreviewText: {
        color: theme.colors.neutral['500'],
        fontSize: 14,
        fontWeight: '600',
    },
    rangePreviewDuration: {
        color: theme.colors.neutral['400'],
        fontSize: 12,
    },
    rangePreviewWarning: {
        color: theme.colors.warnings['300'],
        fontSize: 11,
        fontWeight: '600',
    },
}));
